import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { emptyWaveform, samplesToWaveform } from '../../../../shared/models/voice-waveform.model';
import { SILENT_PLAYBACK, type AudioPlaybackState } from '../../models/audio-playback.model';
import type { IAudioPlaybackProvider } from './i-audio-playback.provider';

/**
 * Ogni quanti millisecondi si rilegge il punto della riproduzione. È il passo della transizione delle barre
 * (`voice-waveform.component.scss`): guardarlo a ogni fotogramma darebbe la stessa immagine costando
 * quattro volte tanto in change detection.
 */
const PROGRESS_INTERVAL_MS = 60;

@Injectable()
export class DocumentAudioPlaybackProvider implements IAudioPlaybackProvider {
  private readonly window = inject(DOCUMENT).defaultView;
  private readonly zone = inject(NgZone);

  private current: HTMLAudioElement | null = null;
  private currentUrl: string | null = null;
  private frame: number | null = null;

  /** L'onda della traccia in corso e il punto raggiunto: si annunciano sempre insieme. */
  private levels: readonly number[] = emptyWaveform();
  private progress = 0;

  private readonly playbackSource = new Subject<AudioPlaybackState>();
  readonly playback$: Observable<AudioPlaybackState> = this.playbackSource.asObservable();

  constructor() {
    // Un audio lasciato in riproduzione sopravvivrebbe alla pagina che lo ha chiesto: chi esce dalla chat
    // mentre un messaggio viene letto se lo sentirebbe finire addosso alla schermata dopo.
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  isAvailable(): boolean {
    return !!this.window?.Audio && !!this.window.URL?.createObjectURL;
  }

  play(audio: Blob): void {
    const win = this.window;
    if (!win?.Audio) return;

    this.stop();

    const url = win.URL.createObjectURL(audio);
    const element = new win.Audio(url);
    this.current = element;
    this.currentUrl = url;
    this.levels = emptyWaveform();
    this.progress = 0;

    element.addEventListener('ended', () => this.release(), { once: true });

    // L'annuncio parte prima della promise: `play()` risolve quando la riproduzione è già cominciata, e
    // aspettarla lascerebbe la bolla col testo per i primi istanti della propria lettura.
    this.playbackSource.next({ ...SILENT_PLAYBACK, playing: true });
    this.drawTrackOf(audio, element);
    this.followProgressOf(element);

    // La riproduzione può essere rifiutata dal browser — un documento con cui l'utente non ha ancora
    // interagito non suona — e il rifiuto arriva come promise respinta, non come eccezione.
    void element.play().catch(() => this.release());
  }

  stop(): void {
    this.current?.pause();
    this.release();
  }

  /**
   * Disegna l'onda della traccia intera dai suoi campioni. Si decodifica una volta sola, all'inizio, e non
   * si analizza il segnale mentre suona: cio' che deve scorrere e' il colore sull'onda ferma, non l'onda.
   *
   * Il contesto serve solo a decodificare e si chiude subito: l'audio esce dal proprio elemento, non da
   * qui, quindi nessuno gli toglie l'uscita e una decodifica che fallisce lascia la lettura al suo posto —
   * con un'onda piatta, che è meno di quel che si voleva ma non è un errore da mostrare.
   */
  private drawTrackOf(audio: Blob, element: HTMLAudioElement): void {
    const audioContext = this.window?.AudioContext;
    if (!audioContext || typeof audio.arrayBuffer !== 'function') return;

    const context = new audioContext();

    void audio
      .arrayBuffer()
      .then((bytes) => context.decodeAudioData(bytes))
      .then((decoded) => {
        // La decodifica dura quanto vuole: nel frattempo la lettura puo' essere finita, o puo' esserne
        // cominciata un'altra. L'onda di una traccia che non sta piu' suonando non va annunciata.
        if (this.current !== element) return;

        this.levels = samplesToWaveform(decoded.getChannelData(0));
        this.announce();
      })
      .catch(() => undefined)
      .finally(() => void context.close().catch(() => undefined));
  }

  /**
   * Segue il punto della riproduzione finché suona. È l'unica cosa che si muove: le barre restano quelle
   * della traccia, e a cambiare è quante ne sono già state attraversate.
   */
  private followProgressOf(element: HTMLAudioElement): void {
    const win = this.window;
    if (!win?.requestAnimationFrame) return;

    let lastReadAt = 0;

    const follow = (now: number): void => {
      if (this.current !== element) return;

      if (now - lastReadAt >= PROGRESS_INTERVAL_MS) {
        lastReadAt = now;
        const reached = progressOf(element);

        if (reached !== this.progress) {
          this.progress = reached;
          this.zone.run(() => this.announce());
        }
      }

      this.frame = win.requestAnimationFrame(follow);
    };

    // Fuori dalla zona: ogni fotogramma — sessanta al secondo — faceva girare il change detection
    // dell'applicazione intera, anche quando l'avanzamento non era cambiato. Si rientra solo per annunciarlo.
    this.zone.runOutsideAngular(() => {
      this.frame = win.requestAnimationFrame(follow);
    });
  }

  private announce(): void {
    this.playbackSource.next({ playing: true, levels: this.levels, progress: this.progress });
  }

  /** L'URL dell'oggetto resta allocato finché non lo si revoca: ogni messaggio letto costerebbe un blob. */
  private release(): void {
    const wasPlaying = this.current !== null;

    if (this.frame !== null) this.window?.cancelAnimationFrame(this.frame);
    this.frame = null;

    if (this.currentUrl) this.window?.URL.revokeObjectURL(this.currentUrl);
    this.current = null;
    this.currentUrl = null;
    this.levels = emptyWaveform();
    this.progress = 0;

    if (wasPlaying) this.playbackSource.next(SILENT_PLAYBACK);
  }
}

/**
 * Quanta traccia è passata, in [0, 1]. Una durata che il browser non conosce ancora — `NaN` finché i
 * metadati non sono arrivati — non è un punto zero: è l'assenza di un punto, e vale come inizio.
 */
function progressOf(element: HTMLAudioElement): number {
  const duration = element.duration;
  if (!Number.isFinite(duration) || duration <= 0) return 0;

  return Math.min(1, Math.max(0, element.currentTime / duration));
}
